import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import WhatsAppAdminButton from './WhatsAppAdminButton';
import SafetyDisclaimer from './SafetyDisclaimer';
import { getStoredUnlock, saveVerifiedUnlock, isUnlockStillValid, unlockStorageKey } from '../utils/unlockAccess';

const ADMIN_MPESA_NUMBER = '0714 644 890';
const CHARSET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no 0/O/1/I to avoid confusion

const generateReferenceCode = () => {
  let code = '';
  for (let i = 0; i < 6; i++) code += CHARSET[Math.floor(Math.random() * CHARSET.length)];
  return code;
};

const inputStyle: React.CSSProperties = {
  width: '100%', background: 'var(--dark-3)', border: '1px solid rgba(201,168,76,0.2)',
  color: 'var(--text)', padding: '12px 16px', borderRadius: '4px', fontSize: '14px', outline: 'none',
};

type Props = {
  listingId: string;
  listingTable: string;
  listingTitle: string;
  unlockFee: number;
  onClose: () => void;
};

type Stage =
  | 'restoring'
  | 'stk-entry' | 'stk-sending' | 'stk-waiting' | 'stk-failed'
  | 'manual-instructions' | 'manual-submitting' | 'manual-waiting' | 'manual-rejected'
  | 'verified';

type Contact = { google_maps_link: string; whatsapp: string; second_whatsapp?: string | null };

const UnlockPaymentModal = ({ listingId, listingTable, listingTitle, unlockFee, onClose }: Props) => {
  const [stage, setStage] = useState<Stage>('restoring');
  const [phone, setPhone] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [referenceCode, setReferenceCode] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const [contact, setContact] = useState<Contact | null>(null);
  const [callbackDate, setCallbackDate] = useState('');
  const [callbackTime, setCallbackTime] = useState('');
  const pollCount = useRef(0);

  const isLand = listingTable === 'land';
  const contactLabel = listingTable === 'land' ? 'Agent' : listingTable === 'cars' ? 'Dealer' : listingTable === 'houses_for_sale' ? 'Seller' : 'Caretaker';
  const storageKey = unlockStorageKey(listingTable, listingId);

  const revealContact = async (ref: string) => {
    const rpcName = (listingTable === 'houses_for_sale' || listingTable === 'cars') ? 'get_unlocked_contact_extra' : 'get_unlocked_contact';
    const { data } = await supabase.rpc(rpcName, { ref });
    if (data && data.length > 0) {
      setContact(data[0]);
      setStage('verified');
      saveVerifiedUnlock({
        ref,
        method: getStoredUnlock(listingTable, listingId)?.method || 'stk',
        verified: true,
        verifiedAt: Date.now(),
        listingTable,
        listingId,
        listingTitle,
      });
    } else {
      // The saved record didn't check out server-side (e.g. deleted admin-side) — start over.
      localStorage.removeItem(storageKey);
      setStage('stk-entry');
    }
  };

  // On open, check if this listing was already unlocked in this browser
  // (within the last 24 hours) — if so, skip straight to showing the
  // contact instead of asking to pay again. Otherwise resume any
  // in-flight (not-yet-verified) request, or start fresh.
  useEffect(() => {
    const saved = getStoredUnlock(listingTable, listingId);
    if (saved?.verified && isUnlockStillValid(saved)) {
      setReferenceCode(saved.ref);
      revealContact(saved.ref);
    } else if (saved?.verified) {
      // Was verified but the 24-hour window has passed.
      localStorage.removeItem(storageKey);
      setStage('stk-entry');
    } else if (saved) {
      setReferenceCode(saved.ref);
      setStage(saved.method === 'stk' ? 'stk-waiting' : 'manual-waiting');
    } else {
      setStage('stk-entry');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // ---- STK PUSH FLOW ----
  const handlePayWithMpesa = async () => {
    setError('');
    if (!/^0\d{9}$/.test(phone.replace(/\s/g, ''))) {
      setError('Enter a valid M-Pesa number, e.g. 0712345678.');
      return;
    }
    setStage('stk-sending');

    let normalized = phone.replace(/\D/g, '');
    if (normalized.startsWith('0')) normalized = '254' + normalized.slice(1);
    else if (!normalized.startsWith('254')) normalized = '254' + normalized;

    const ref = generateReferenceCode();
    const { error: insertError } = await supabase.from('unlock_requests').insert({
      reference_code: ref,
      listing_table: listingTable,
      listing_id: listingId,
      listing_title: listingTitle,
      buyer_phone: normalized,
      amount: unlockFee,
      status: 'pending',
      payment_method: 'stk',
    });

    if (insertError) {
      setError('Something went wrong. Please try again.');
      setStage('stk-entry');
      return;
    }

    const { data: fnData, error: fnError } = await supabase.functions.invoke('stk-push', {
      body: { reference_code: ref, phone_number: normalized, amount: unlockFee },
    });

    if (fnError || !fnData?.success) {
      setError(fnData?.error || 'Could not send the payment prompt. Try again, or pay manually below.');
      setStage('stk-failed');
      setReferenceCode(ref);
      return;
    }

    setReferenceCode(ref);
    localStorage.setItem(storageKey, JSON.stringify({ ref, method: 'stk' }));
    pollCount.current = 0;
    setStage('stk-waiting');
  };

  const pollStkStatus = useCallback(async () => {
    if (!referenceCode) return;
    setChecking(true);
    const { data } = await supabase.functions.invoke('stk-status', { body: { reference_code: referenceCode } });
    setChecking(false);
    pollCount.current += 1;

    if (data?.state === 'COMPLETE' || data?.state === 'COMPLETED' || data?.state === 'SUCCESS') {
      revealContact(referenceCode);
    } else if (data?.state === 'FAILED' || data?.state === 'CANCELLED') {
      setStage('stk-failed');
      setError('Payment wasn\u2019t completed on M-Pesa. You can try again, or pay manually below.');
    } else if (pollCount.current >= 18) {
      setStage('stk-failed');
      setError('This is taking longer than expected. You can try again, or pay manually below.');
    }
  }, [referenceCode]);

  useEffect(() => {
    if (stage !== 'stk-waiting') return;
    const interval = setInterval(pollStkStatus, 5000);
    pollStkStatus();
    return () => clearInterval(interval);
  }, [stage, pollStkStatus]);

  // ---- MANUAL FALLBACK FLOW ----
  const handleManualSubmit = async () => {
    setError('');
    if (!/^0\d{9}$/.test(phone.replace(/\s/g, ''))) {
      setError('Enter the phone number you\u2019re paying FROM, e.g. 0712345678.');
      return;
    }
    setStage('manual-submitting');

    let screenshotPath: string | null = null;
    if (screenshot) {
      const path = `${Date.now()}-${screenshot.name}`;
      const { error: uploadError } = await supabase.storage.from('payment-proofs').upload(path, screenshot);
      if (!uploadError) screenshotPath = path;
    }

    const ref = generateReferenceCode();
    const { error: insertError } = await supabase.from('unlock_requests').insert({
      reference_code: ref,
      listing_table: listingTable,
      listing_id: listingId,
      listing_title: listingTitle,
      buyer_phone: phone.replace(/\s/g, ''),
      amount: unlockFee,
      screenshot_path: screenshotPath,
      status: 'pending',
      payment_method: 'manual',
    });

    if (insertError) {
      setError('Something went wrong submitting your request. Please try again.');
      setStage('manual-instructions');
      return;
    }

    setReferenceCode(ref);
    localStorage.setItem(storageKey, JSON.stringify({ ref, method: 'manual' }));
    setStage('manual-waiting');
  };

  const checkManualStatus = useCallback(async (ref: string) => {
    setChecking(true);
    const { data } = await supabase.rpc('check_unlock_status', { ref });
    setChecking(false);
    if (!data || data.length === 0) return;
    if (data[0].status === 'verified') revealContact(ref);
    else if (data[0].status === 'rejected') setStage('manual-rejected');
  }, []);

  useEffect(() => {
    if (stage !== 'manual-waiting' || !referenceCode) return;
    checkManualStatus(referenceCode);
    const interval = setInterval(() => checkManualStatus(referenceCode), 20000);
    return () => clearInterval(interval);
  }, [stage, referenceCode, checkManualStatus]);

  const requestCallback = () => {
    if (!contact?.whatsapp) return;
    const when = callbackDate && callbackTime
      ? `on ${callbackDate} at ${callbackTime}`
      : callbackDate
      ? `on ${callbackDate}`
      : callbackTime
      ? `at ${callbackTime}`
      : 'at your earliest convenience';
    const message = `Hi, I'd like to request a callback regarding "${listingTitle}" ${when}.`;
    window.open(`https://wa.me/${contact.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--dark-3)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '12px', padding: '32px', maxWidth: '420px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>

        {stage === 'restoring' && (
          <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>Checking access...</p>
        )}

        {stage === 'stk-entry' && (
          <>
            <h3 style={{ color: 'var(--gold)', fontSize: '18px', marginBottom: '8px' }}>Unlock This Listing</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '16px' }}>
              Pay <strong style={{ color: 'var(--gold)' }}>KSh {unlockFee}</strong> via M-Pesa to see the exact location and {isLand ? 'agent' : 'caretaker'} contact.
            </p>
            <label style={{ color: 'var(--text-muted)', fontSize: '11px', display: 'block', marginBottom: '6px' }}>M-Pesa Number</label>
            <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="0712345678" style={{ ...inputStyle, marginBottom: '12px' }} />
            {error && <p style={{ color: '#f87171', fontSize: '13px', marginBottom: '12px' }}>{error}</p>}
            <button onClick={handlePayWithMpesa} className="btn-primary" style={{ width: '100%', padding: '12px', marginBottom: '10px' }}>
              📲 Pay with M-Pesa
            </button>
            <button onClick={() => { setError(''); setStage('manual-instructions'); }} style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}>
              Having trouble? Pay manually instead
            </button>
            <button onClick={onClose} style={{ width: '100%', marginTop: '10px', background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: '12px', cursor: 'pointer' }}>
              Cancel
            </button>
          </>
        )}

        {stage === 'stk-sending' && (
          <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>Sending payment prompt to your phone...</p>
        )}

        {stage === 'stk-waiting' && (
          <>
            <p style={{ fontSize: '32px', textAlign: 'center', marginBottom: '8px' }}>📲</p>
            <h3 style={{ color: 'var(--gold)', fontSize: '18px', marginBottom: '8px', textAlign: 'center' }}>Check Your Phone</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.6, marginBottom: '16px', textAlign: 'center' }}>
              Enter your M-Pesa PIN on the prompt sent to <strong style={{ color: 'var(--text)' }}>{phone}</strong>.{checking ? ' Checking...' : ''}
            </p>
            <button onClick={onClose} style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: '12px', cursor: 'pointer' }}>
              Close (we'll keep checking automatically)
            </button>
          </>
        )}

        {stage === 'stk-failed' && (
          <>
            <h3 style={{ color: '#f87171', fontSize: '18px', marginBottom: '12px', textAlign: 'center' }}>Payment Not Completed</h3>
            {error && <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '20px', textAlign: 'center' }}>{error}</p>}
            <button onClick={() => { setError(''); setStage('stk-entry'); }} className="btn-primary" style={{ width: '100%', marginBottom: '10px' }}>
              Try M-Pesa Again
            </button>
            <button onClick={() => { setError(''); setStage('manual-instructions'); }} className="btn-ghost" style={{ width: '100%' }}>
              Pay Manually Instead
            </button>
          </>
        )}

        {stage === 'manual-instructions' && (
          <>
            <h3 style={{ color: 'var(--gold)', fontSize: '18px', marginBottom: '12px' }}>Manual Payment</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.6, marginBottom: '16px' }}>
              1. Open M-Pesa on your phone<br />
              2. Send Money to <strong style={{ color: 'var(--gold)' }}>{ADMIN_MPESA_NUMBER}</strong><br />
              3. Amount: <strong style={{ color: 'var(--gold)' }}>KSh {unlockFee}</strong><br />
              4. Come back here and enter the number you paid FROM below
            </p>
            <label style={{ color: 'var(--text-muted)', fontSize: '11px', display: 'block', marginBottom: '6px' }}>Your M-Pesa number</label>
            <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="0712345678" style={{ ...inputStyle, marginBottom: '12px' }} />
            <label style={{ color: 'var(--text-muted)', fontSize: '11px', display: 'block', marginBottom: '6px' }}>Payment screenshot (optional, speeds up verification)</label>
            <input type="file" accept="image/*" onChange={e => setScreenshot(e.target.files?.[0] || null)} style={{ ...inputStyle, marginBottom: '16px', padding: '8px' }} />
            {error && <p style={{ color: '#f87171', fontSize: '13px', marginBottom: '12px' }}>{error}</p>}
            <button onClick={handleManualSubmit} className="btn-primary" style={{ width: '100%', marginBottom: '10px' }}>
              I've Paid — Submit for Verification
            </button>
            <button onClick={() => { setError(''); setStage('stk-entry'); }} style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: '12px', cursor: 'pointer' }}>
              ← Back to M-Pesa auto-pay
            </button>
          </>
        )}

        {stage === 'manual-submitting' && (
          <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>Submitting...</p>
        )}

        {stage === 'manual-waiting' && (
          <>
            <p style={{ fontSize: '32px', textAlign: 'center', marginBottom: '8px' }}>⏳</p>
            <h3 style={{ color: 'var(--gold)', fontSize: '18px', marginBottom: '8px', textAlign: 'center' }}>Verifying Your Payment</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.6, marginBottom: '16px', textAlign: 'center' }}>
              Your reference code is <strong style={{ color: 'var(--gold)' }}>{referenceCode}</strong>.<br />
              We'll unlock this listing as soon as your payment is confirmed — usually within a few hours.
            </p>
            <button onClick={() => checkManualStatus(referenceCode)} disabled={checking} className="btn-ghost" style={{ width: '100%', marginBottom: '10px' }}>
              {checking ? 'Checking...' : 'Check Status Now'}
            </button>
            <button onClick={onClose} style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: '12px', cursor: 'pointer' }}>
              Close (we'll keep checking automatically)
            </button>
          </>
        )}

        {stage === 'manual-rejected' && (
          <>
            <h3 style={{ color: '#f87171', fontSize: '18px', marginBottom: '12px', textAlign: 'center' }}>Couldn't Verify Payment</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.6, marginBottom: '16px', textAlign: 'center' }}>
              We couldn't confirm this payment. If you did pay, reach out below with your reference code <strong style={{ color: 'var(--gold)' }}>{referenceCode}</strong>.
            </p>
            <WhatsAppAdminButton />
          </>
        )}

        {stage === 'verified' && contact && (
          <>
            <p style={{ fontSize: '32px', textAlign: 'center', marginBottom: '8px' }}>✅</p>
            <h3 style={{ color: '#4ade80', fontSize: '18px', marginBottom: '4px', textAlign: 'center' }}>Unlocked!</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '11px', textAlign: 'center', marginBottom: '16px' }}>
              You can reopen this listing on this device for the next 24 hours without paying again.
            </p>

            <SafetyDisclaimer />

            {contact.whatsapp && (
              <a href={`https://wa.me/${contact.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="btn-primary"
                style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginBottom: '10px' }}>
                💬 Chat with {contactLabel}
              </a>
            )}

            {isLand && contact.second_whatsapp && (
              <a href={`https://wa.me/${contact.second_whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="btn-ghost"
                style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginBottom: '16px' }}>
                💬 Alternative Contact
              </a>
            )}

            {isLand && contact.whatsapp && (
              <div style={{ borderTop: '1px solid rgba(201,168,76,0.15)', paddingTop: '16px', marginTop: '6px', marginBottom: '16px' }}>
                <p style={{ color: 'var(--gold)', fontSize: '12px', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '10px', fontWeight: 600 }}>
                  📅 Request a Callback
                </p>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <input type="date" value={callbackDate} onChange={e => setCallbackDate(e.target.value)} style={{ ...inputStyle, fontSize: '13px' }} />
                  <input type="time" value={callbackTime} onChange={e => setCallbackTime(e.target.value)} style={{ ...inputStyle, fontSize: '13px' }} />
                </div>
                <button onClick={requestCallback} className="btn-ghost" style={{ width: '100%' }}>
                  Send Callback Request
                </button>
              </div>
            )}

            {contact.google_maps_link && (
              <a href={contact.google_maps_link} target="_blank" rel="noopener noreferrer"
                style={{ display: 'block', textAlign: 'center', textDecoration: 'none', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '4px', padding: '12px', fontSize: '13px' }}>
                📍 View Site Location
              </a>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UnlockPaymentModal;
